import { AutomationBullet } from "@/shared/data/home.v2.data";
import {
  faChartLine,
  faCheck,
  faClipboardList,
  faEnvelope,
  faFlag,
  faFlask,
  faGlobe,
  faGraduationCap,
  faHeadset,
  faKey,
  faLightbulb,
  faMoon,
  faQuestion,
  faReceipt,
  faShareNodes,
  faTag,
  faWindowMaximize,
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";

/**
 * AutomationAccordionItem — reusable accordion row.
 *
 * Anatomy:
 *   • Button row — Inter-Medium (tight) title on the left, chevron on
 *     the right. Chevron flips up ↔ down based on `isOpen`.
 *   • Optional description paragraph and bullet list when open. Each
 *     bullet renders with a FontAwesome Pro Regular icon on the left;
 *     the icon identifier in the bullet data is mapped to a concrete
 *     IconDefinition via `bulletIconMap` below.
 *   • Bottom progress-bar rail — a 2px track that either shows the
 *     default divider color (when `isProgressActive` is false) or
 *     animates a primary-color fill over 5s (when true). This replaces
 *     the static `divide-y` divider so a ticking auto-advance cycle
 *     reads as "progress through this feature".
 *
 * Pass a unique `progressKey` each time the auto-advance timer resets
 * so the CSS animation restarts from 0.
 */

/**
 * String identifier → FontAwesome Pro Regular IconDefinition. Kept in
 * this component (not in the data file) so the data layer stays free
 * of FontAwesome imports. Unknown identifiers fall back to a bullet
 * point dot.
 */
const bulletIconMap: Record<string, IconDefinition> = {
  question: faQuestion,
  tag: faTag,
  clipboard: faClipboardList,
  moon: faMoon,
  key: faKey,
  receipt: faReceipt,
  headset: faHeadset,
  globe: faGlobe,
  window: faWindowMaximize,
  envelope: faEnvelope,
  share: faShareNodes,
  flask: faFlask,
  chart: faChartLine,
  graduation: faGraduationCap,
  flag: faFlag,
  lightbulb: faLightbulb,
  check: faCheck,
};

export type AutomationAccordionItemProps = {
  id: string;
  title: string;
  description?: string;
  bullets?: AutomationBullet[];
  /** Header above the bullet list. Defaults to "How It Works:". Pass ""
   *  to suppress the label entirely. */
  bulletsHeading?: string;
  /** Optional closing one-liner rendered under the bullets. */
  footerLine?: string;
  isOpen: boolean;
  onToggle: (id: string) => void;
  /** When true, the bottom rail animates a 5s primary-color fill. */
  isProgressActive?: boolean;
  /** Bumped externally (e.g. via React `key`) to restart the fill. */
  progressKey?: number | string;
  /** Show the bottom rail. Defaults to true. Pass false for the last row. */
  showDivider?: boolean;
  className?: string;
};

const AutomationAccordionItem = ({
  id,
  title,
  description,
  bullets,
  bulletsHeading = "How It Works:",
  footerLine,
  isOpen,
  onToggle,
  isProgressActive = false,
  progressKey,
  showDivider = true,
  className,
}: AutomationAccordionItemProps) => {
  return (
    <div className={classNames("py-4", className)}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="flex w-full items-center justify-between gap-4 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="font-inter text-[16px] font-medium tracking-tight text-neutralBlack md:text-[18px]">
          {title}
        </h3>
        <i
          className={classNames(
            "fa-solid shrink-0 text-neutralBlack/60 transition-transform duration-200",
            isOpen ? "fa-chevron-up" : "fa-chevron-down"
          )}
          aria-hidden="true"
        />
      </button>

      {isOpen ? (
        <div className="mt-2">
          {description ? (
            <p className="font-inter text-[14px] leading-relaxed text-darkSlate04 md:text-[15px]">
              {description}
            </p>
          ) : null}
          {bullets && bullets.length ? (
            <>
              {bulletsHeading ? (
                <div className="mt-3 font-inter text-[13px] font-medium text-darkSlate04">
                  {bulletsHeading}
                </div>
              ) : null}
              <ul className="mt-2 space-y-2">
                {bullets.map((b) => {
                  const iconDef = bulletIconMap[b.icon];
                  return (
                    <li
                      key={b.text}
                      className="flex items-start gap-3 font-inter text-[13px] text-darkSlate04"
                    >
                      <span className="mt-[2px] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slateLight text-darkSlate04">
                        {iconDef ? (
                          <FontAwesomeIcon
                            icon={iconDef}
                            style={{ fontSize: 12 }}
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-darkSlate01" />
                        )}
                      </span>
                      <span className="pt-[2px]">{b.text}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
          {footerLine ? (
            <p className="mt-3 font-inter text-[13px] italic text-darkSlate04">
              {footerLine}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Progress-bar divider. Lives at the bottom of the row so the
          track acts as the separator between rows. When this row is the
          active/auto-advancing one, a primary-color bar fills left→right
          over 5s. Otherwise the track shows the default divider tint. */}
      {showDivider ? (
        <div className="mt-4 h-px overflow-hidden bg-slateStroke">
          {isProgressActive ? (
            <span
              key={progressKey}
              className="accordion-row-progress-fill block h-full w-full bg-brandPrimary"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AutomationAccordionItem;
